import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import flightIcon from "../../assets/flighticon.png";
import hotelIcon from "../../assets/hotelicon.png";
import visaIcon from "../../assets/visaicon.png";
import { useNavigate } from "react-router-dom";
import { getExploreContent } from "../../services/api";

// ── Types (mirror the content_blocks API shape) ────────────────────────────────
type Kind = "header" | "offer" | "hero" | "destination";
interface CtaButton { label: string; icon?: string; action?: string; }
interface BlockData {
  size?: "wide" | "normal";
  imagePosition?: "left" | "bottom";
  buttons?: CtaButton[];
  cities?: string[];
  tags?: string[];
}
export interface ExploreBlock {
  id: number;
  kind: Kind;
  title: string | null;
  subtitle: string | null;
  body: string | null;
  mediaUrl: string | null;
  mediaType: "image" | "video" | null;
  data: BlockData;
}

// ── Fallback content — identical to what the section used to hard-code. ─────────
// Rendered instantly (zero layout shift) and kept if the API is unreachable, so
// the homepage never breaks. Replaced by live data when it arrives.
const FALLBACK_BLOCKS: ExploreBlock[] = [
  { id: -1, kind: "header", title: "Build your travel with us.", subtitle: "Find exclusive offers and deals.", body: null, mediaUrl: null, mediaType: null, data: {} },
  { id: -2, kind: "offer", title: "Pay with Careem and get Rs. 100 Cashback", subtitle: "T&C Apply", body: null, mediaUrl: "/assets/flights/careempay.png", mediaType: "image", data: { size: "wide", imagePosition: "left" } },
  { id: -3, kind: "offer", title: "Save on tickets to Kochi", subtitle: null, body: null, mediaUrl: "/assets/flights/etihad.png", mediaType: "image", data: { size: "normal", imagePosition: "bottom" } },
  { id: -4, kind: "hero", title: "With you at every step of your journey.", subtitle: null, body: null, mediaUrl: "/assets/flights/exploreguy.jpg", mediaType: "image", data: { buttons: [
    { label: "Book Flights", icon: "flight", action: "scrollTop" },
    { label: "Book Hotels", icon: "hotel", action: "scrollTop" },
    { label: "Visa Assistance", icon: "visa", action: "/visa-assistance" },
  ] } },
  { id: -5, kind: "destination", title: "Barcelona", subtitle: "Find a destination for your next adventure.",
    body: "Barcelona, a vibrant coastal city in Spain, is a captivating blend of ancient history and contemporary culture. Renowned for its architectural marvels, stunning beaches, and lively atmosphere, Barcelona offers something for everyone.",
    mediaUrl: "/assets/flights/barcelona.mp4", mediaType: "video",
    data: { cities: ["Barcelona", "Georgia", "Turkey", "Singapore"], tags: ["Sagrada Familia", "Passeig de Gràcia", "Bari Gotic"], buttons: [
      { label: "Book Flights", icon: "flight", action: "scrollTop" },
      { label: "Book Hotels", icon: "hotel", action: "scrollTop" },
    ] } },
];

const ICON_MAP: Record<string, string> = { flight: flightIcon, hotel: hotelIcon, visa: visaIcon };

// Scroll-reveal wrapper. Disabled in preview mode (preview lives inside a
// scroll container where whileInView detection is unreliable).
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; preview?: boolean }> = ({ children, delay = 0, preview }) => {
  if (preview) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const ExploreSection: React.FC<{ blocks?: ExploreBlock[]; preview?: boolean }> = ({ blocks: blocksProp, preview }) => {
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<ExploreBlock[]>(FALLBACK_BLOCKS);

  useEffect(() => {
    if (blocksProp) return; // preview / controlled mode: don't fetch
    let alive = true;
    getExploreContent()
      .then((res) => {
        // Any successful response wins — including an empty one, so hiding every
        // block genuinely clears the section instead of resurrecting defaults.
        // The fallback is kept ONLY when the request itself fails (below).
        if (alive && Array.isArray(res?.blocks)) {
          setFetched(res.blocks as ExploreBlock[]);
        }
      })
      .catch((err) => console.warn("[ExploreSection] falling back to defaults:", err?.message));
    return () => { alive = false; };
  }, [blocksProp]);

  const blocks = blocksProp ?? fetched;

  // Everything hidden → section disappears on the live site.
  if (blocks.length === 0) {
    return preview
      ? <div className="py-16 text-center text-sm text-gray-400">No active blocks — the Explore section is hidden on the live site.</div>
      : null;
  }

  const runAction = (action?: string) => {
    if (preview) return; // don't hijack navigation while previewing
    if (!action || action === "scrollTop") window.scrollTo({ top: 0, behavior: "smooth" });
    else if (action.startsWith("/")) navigate(action);
    else if (/^https?:/i.test(action)) window.open(action, "_blank", "noopener");
  };

  const header = blocks.find((b) => b.kind === "header");
  const offers = blocks.filter((b) => b.kind === "offer");
  const heroes = blocks.filter((b) => b.kind === "hero");
  const destinations = blocks.filter((b) => b.kind === "destination");

  const CtaButtons = ({ buttons, base }: { buttons?: CtaButton[]; base: string }) => (
    <>
      {(buttons ?? []).map((btn, i) => (
        <button key={i} className={base} onClick={() => runAction(btn.action)}>
          <span>{btn.label}</span>
          {btn.icon && ICON_MAP[btn.icon] && (
            <img src={ICON_MAP[btn.icon]} alt="" className="w-[34px] h-[34px] ml-2" />
          )}
        </button>
      ))}
    </>
  );

  return (
    <section className="bg-white text-black margin-x-auto max-w-screen-xl mx-auto font-sans px-4 py-3 space-y-6">
      {/* Header */}
      {header && (
        <Reveal preview={preview}>
          <div className="text-left space-y-1">
            <h2 className="text-black text-lg sm:text-xl md:text-2xl font-sora font-bold">{header.title}</h2>
            {header.subtitle && <p className="text-sm sm:text-base text-heading">{header.subtitle}</p>}
          </div>
        </Reveal>
      )}

      {/* Offers grid */}
      {offers.length > 0 && (
        <Reveal preview={preview} delay={0.05}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 min-h-[200px] items-stretch transition-all duration-300">
            {offers.map((offer) => {
              const wide = offer.data.size === "wide";
              const imageLeft = offer.data.imagePosition === "left";
              return (
                <div
                  key={offer.id}
                  className={`${wide ? "md:col-span-2" : ""} bg-gradient-to-b from-[#0B2F9F] to-[#3F6AEF] text-white rounded-[20px]
                    shadow-md transform transition-all duration-300 hover:scale-[1.02]
                    ${imageLeft
                      ? "px-6 sm:px-12 py-4 flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
                      : "px-4 sm:px-6 py-4 flex flex-col items-center justify-center text-center"}`}
                >
                  {imageLeft ? (
                    <>
                      {offer.mediaUrl && <img src={offer.mediaUrl} alt="" className="w-[80px] sm:w-[100px] h-auto rounded-2xl" />}
                      <div className="text-center sm:text-left">
                        <p className="font-semibold text-sm sm:text-superbase font-sora">{offer.title}</p>
                        {offer.subtitle && <p className="text-xs sm:text-sm mt-1 font-sans">{offer.subtitle}</p>}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold font-sora text-sm sm:text-superbase mb-2">{offer.title}</p>
                      {offer.mediaUrl && <img src={offer.mediaUrl} alt="" className="w-[120px] sm:w-[160px] h-auto object-contain" />}
                      {offer.subtitle && <p className="text-xs sm:text-sm mt-1 font-sans">{offer.subtitle}</p>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Reveal>
      )}

      {/* Hero banners */}
      {heroes.map((hero) => (
        <Reveal key={hero.id} preview={preview} delay={0.05}>
          <div className="relative w-auto h-[300px] sm:h-[400px] rounded-[24px] overflow-hidden shadow-[0px_1.53px_1.53px_rgba(0,0,0,0.25)] transition-all duration-300">
            {hero.mediaUrl && (
              hero.mediaType === "video"
                ? <video src={hero.mediaUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                : <img src={hero.mediaUrl} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/40 text-white flex flex-col sm:flex-row items-center justify-between p-4 sm:px-8 sm:py-10 gap-6">
              <h2 className="text-2xl sm:text-4xl font-sora font-bold leading-tight max-w-md text-center sm:text-left whitespace-pre-line">
                {hero.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full sm:w-auto">
                <CtaButtons
                  buttons={hero.data.buttons}
                  base="bg-gradient-to-b from-[#0B2F9F] to-[#3F6AEF] text-white w-full sm:w-[180px] h-[60px] sm:h-[90px] rounded-[24px] px-4 py-2 flex items-center justify-between shadow-md font-sans text-sm font-semibold transform transition-all duration-300 hover:scale-[1.05]"
                />
              </div>
            </div>
          </div>
        </Reveal>
      ))}

      {/* Destination blocks */}
      {destinations.map((dest) => (
        <Reveal key={dest.id} preview={preview} delay={0.05}>
          <DestinationBlock dest={dest} runAction={runAction} />
        </Reveal>
      ))}
    </section>
  );
};

// ── Destination sub-component (has its own active-city state) ───────────────────
const DestinationBlock: React.FC<{ dest: ExploreBlock; runAction: (a?: string) => void }> = ({ dest, runAction }) => {
  const cities = dest.data.cities ?? [];
  const [activeCity, setActiveCity] = useState<string>(cities[0] ?? "");

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-stretch">
      {/* City selector */}
      <div className="bg-gradient-to-b from-[#0B2F9F] to-[#3F6AEF] text-white rounded-[24px] p-4 sm:p-6 w-full flex flex-col gap-3 sm:gap-5">
        {dest.subtitle && (
          <h3 className="font-semibold font-sora text-2xl sm:text-3xl mb-2 whitespace-pre-line">{dest.subtitle}</h3>
        )}
        {cities.map((city) => (
          <button
            key={city}
            onClick={() => setActiveCity(city)}
            className={`w-full py-2 h-[50px] sm:h-[60px] rounded-full font-sans font-semibold text-white
              ${city === activeCity
                ? "bg-gradient-to-b from-[#001453] to-[#000000]"
                : "bg-gradient-to-b from-[#FCFDFF] to-[#3F6AEF]"}
              shadow-md transform transition-all duration-300 hover:scale-[1.02]`}
          >
            {city}
          </button>
        ))}
      </div>

      {/* Preview card */}
      <div className="md:col-span-2 bg-card rounded-[24px] overflow-hidden shadow">
        <div className="p-4 space-y-2">
          {dest.mediaUrl && (
            dest.mediaType === "video"
              ? <video src={dest.mediaUrl} autoPlay muted loop playsInline preload="auto" className="w-full h-[200px] sm:h-80 object-cover rounded-lg" />
              : <img src={dest.mediaUrl} alt={dest.title ?? ""} className="w-full h-[200px] sm:h-80 object-cover rounded-lg" />
          )}
          <div className="flex flex-wrap gap-2">
            {(dest.data.buttons ?? []).map((btn, i) => (
              <button key={i} className="bg-primary flex items-center justify-center gap-2 text-white px-4 py-2 rounded-full text-sm shadow-md" onClick={() => runAction(btn.action)}>
                {btn.label}
                {btn.icon && ICON_MAP[btn.icon] && <img src={ICON_MAP[btn.icon]} alt="" className="w-[34px] h-[34px] ml-2" />}
              </button>
            ))}
          </div>
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-10">
            {dest.body && <p className="text-heading text-sm sm:text-base font-semibold">{dest.body}</p>}
            <div className="flex flex-wrap items-start content-start gap-2 text-xs sm:text-sm">
              {(dest.data.tags ?? []).map((tag) => (
                <button key={tag} className="w-[150px] shrink-0 text-white text-[13px] bg-primary px-3 py-1 rounded-full">{tag}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreSection;
